import { Injectable } from '@angular/core';
import { ColumnModel, GridComponent } from '@syncfusion/ej2-angular-grids';
import { KLARO_COOKIE_NAME } from './consentement/klaro.config';

/**
 * Préfixe des préférences d'affichage dans le localStorage (colonnes des grilles, vues actives,
 * entité de travail...). Évite les collisions avec les autres clés (persistance Syncfusion, filtres)
 * et permet de les épargner quand le reste du localStorage est vidé (nouvelle version, déconnexion).
 */
export const PREFIXE_PREFERENCES = 'preferences:';

/**
 * Préférences d'affichage de l'utilisateur, historiquement stockées en cookies (d'où le nom).
 *
 * Elles vivent désormais dans le localStorage : les cookies étaient renvoyés au serveur à chaque
 * requête, et leur accumulation (un cookie par grille) finissait par dépasser la limite d'en-têtes
 * (~8 Ko) — le serveur refusait alors de servir l'application. Les cookies existants sont migrés
 * vers le localStorage (au démarrage, et à défaut à la première lecture), puis supprimés.
 */
@Injectable({
  providedIn: 'root'
})
export class CookiesService {

  constructor() { }

  public setCookiColumn(grid: GridComponent, cookieName: string){
    const colonnes = grid.columns.map(column => ({
      field: column.field,
      visible: column.visible,
    }));
    this.setCookie(cookieName, colonnes);
  }

  public applyCookies(grid: GridComponent, cookieName: string) {
    const visibilityMap = this.getVisibilityMapFromCookie(cookieName);
    for (let i = 0; i < grid.columns.length; i++) {
      const column = grid.columns[i] as ColumnModel;
      if (column.field && visibilityMap.has(column.field)) {
        column.visible = visibilityMap.get(column.field);
      }
    }
  }

  public getVisibilityMapFromCookie(cookieName: string): Map<string, boolean> {
    const visibilityMap = new Map<string, boolean>();
    this.lireColonnes(cookieName).forEach((col) => {
      if (col?.field) {
        visibilityMap.set(col.field, col.visible);
      }
    });
    return visibilityMap;
  }

  /** Ordre des champs tel que stocké (ordre du tableau). */
  public getColumnOrderFromCookie(cookieName: string): string[] {
    return this.lireColonnes(cookieName)
      .map((col) => col?.field)
      .filter((field: string | undefined): field is string => !!field);
  }

  /** Largeurs de colonnes persistées (field → width px). */
  public getWidthMapFromCookie(cookieName: string): Map<string, number> {
    const widthMap = new Map<string, number>();
    this.lireColonnes(cookieName).forEach((col) => {
      if (!col?.field || col.width == null || col.width === '') {
        return;
      }
      const width = typeof col.width === 'number' ? col.width : Number.parseInt(String(col.width), 10);
      if (Number.isFinite(width) && width > 0) {
        widthMap.set(col.field, width);
      }
    });
    return widthMap;
  }

  public setCookie(cookieName: string, cookieValue: object){
    const valeur = JSON.stringify(cookieValue);
    if (this.ecrireLocalStorage(cookieName, valeur)) {
      // Un ancien cookie du même nom ne doit plus alourdir les en-têtes.
      this.supprimerCookieNavigateur(cookieName);
    } else {
      // localStorage indisponible (navigation privée stricte, quota) : on garde l'ancien comportement.
      this.ecrireCookieNavigateur(cookieName, valeur);
    }
  }

  /** Valeur JSON brute de la préférence (à parser par l'appelant), ou null si absente. Nom exact. */
  public getCookie(cookieName: string): string | null {
    const valeur = this.lireLocalStorage(cookieName);
    if (valeur !== null) {
      return valeur;
    }
    const valeurCookie = this.lireCookieNavigateur(cookieName);
    if (valeurCookie !== null && this.ecrireLocalStorage(cookieName, valeurCookie)) {
      this.supprimerCookieNavigateur(cookieName);
    }
    return valeurCookie;
  }

  /** La préférence existe-t-elle (localStorage ou ancien cookie) ? Remplace les `document.cookie.includes(nom)`. */
  public has(cookieName: string): boolean {
    return this.getCookie(cookieName) !== null;
  }

  public deleteCookie(cookieName: string){
    try {
      localStorage.removeItem(PREFIXE_PREFERENCES + cookieName);
    } catch {
      // localStorage indisponible : rien à y supprimer.
    }
    this.supprimerCookieNavigateur(cookieName);
  }

  /**
   * Migre d'un coup tous les anciens cookies de préférences (valeur JSON objet ou tableau) vers le
   * localStorage, pour alléger tout de suite les en-têtes — y compris pour les grilles que
   * l'utilisateur ne rouvrira pas. Appelé au démarrage de l'application.
   */
  public migrerCookiesVersLocalStorage(): void {
    this.lireTousLesCookiesNavigateur().forEach(([nom, valeur]) => {
      if (nom === KLARO_COOKIE_NAME || !this.estPreferenceJson(valeur)) {
        return;
      }
      // Une valeur déjà migrée prime : le cookie restant est obsolète.
      if (this.lireLocalStorage(nom) !== null || this.ecrireLocalStorage(nom, valeur)) {
        this.supprimerCookieNavigateur(nom);
      }
    });
  }

  /**
   * Vide le localStorage en conservant les préférences d'affichage, comme lorsqu'elles étaient en
   * cookies : elles survivaient à une nouvelle version et à la déconnexion.
   */
  public viderLocalStorageSaufPreferences(): void {
    try {
      const clesASupprimer: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const cle = localStorage.key(i);
        if (cle !== null && !cle.startsWith(PREFIXE_PREFERENCES)) {
          clesASupprimer.push(cle);
        }
      }
      clesASupprimer.forEach((cle) => localStorage.removeItem(cle));
    } catch {
      // localStorage indisponible : rien à vider.
    }
  }

  public forceReload(version){
    window.location.replace(window.location.pathname + '?new-version=' + version);
  }

  private lireColonnes(cookieName: string): Array<{ field?: string; visible?: boolean; width?: number | string }> {
    if (!cookieName) {
      return [];
    }
    const valeur = this.getCookie(cookieName);
    if (valeur === null) {
      return [];
    }
    try {
      const colonnes = JSON.parse(valeur);
      return Array.isArray(colonnes) ? colonnes : [];
    } catch (e) {
      console.warn('Invalid cookie format:', e);
      return [];
    }
  }

  private lireLocalStorage(nom: string): string | null {
    try {
      return localStorage.getItem(PREFIXE_PREFERENCES + nom);
    } catch {
      return null;
    }
  }

  private ecrireLocalStorage(nom: string, valeur: string): boolean {
    try {
      localStorage.setItem(PREFIXE_PREFERENCES + nom, valeur);
      return true;
    } catch {
      return false;
    }
  }

  /** Valeur décodée du cookie de nom exact, ou null. */
  private lireCookieNavigateur(nom: string): string | null {
    const entree = this.lireTousLesCookiesNavigateur().find(([nomCookie]) => nomCookie === nom);
    return entree ? entree[1] : null;
  }

  /** [nom, valeur] de chaque cookie ; la valeur est tout ce qui suit le premier "=" (elle peut en contenir). */
  private lireTousLesCookiesNavigateur(): Array<[string, string]> {
    if (!document.cookie) {
      return [];
    }
    return document.cookie.split('; ').map((ligne) => {
      const separateur = ligne.indexOf('=');
      const nom = separateur === -1 ? ligne : ligne.substring(0, separateur);
      const brute = separateur === -1 ? '' : ligne.substring(separateur + 1);
      return [nom, this.decoder(brute)] as [string, string];
    });
  }

  /** Les anciennes écritures n'encodaient pas la valeur : on décode si possible, sinon on garde le brut. */
  private decoder(valeur: string): string {
    try {
      return decodeURIComponent(valeur);
    } catch {
      return valeur;
    }
  }

  private estPreferenceJson(valeur: string): boolean {
    try {
      const donnees = JSON.parse(valeur);
      return donnees !== null && typeof donnees === 'object';
    } catch {
      return false;
    }
  }

  private ecrireCookieNavigateur(nom: string, valeur: string): void {
    const date = new Date();
    date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
    document.cookie = `${nom}=${valeur}; expires=${date.toUTCString()}; path=/`;
  }

  private supprimerCookieNavigateur(nom: string): void {
    document.cookie = `${nom}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}
